import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/cn";
import { toOutcomeTone, toSeverityTone } from "@/lib/tone";

// Stage 5: local replacement for @datasirpi/ui's <StatCard>. Keyboard model
// is the vendor's (read from the bundle, not guessed): onClick makes the
// tile a role="button" with Enter/Space activation. Dropped the dark-mode
// neon edge/glow — already force-disabled repo-wide, see Card.tsx's header.
//
// 2026-09-21: DOM re-ordered to the reference metric tile — label + icon on
// one line, figure beneath. The prop surface is unchanged, so all 57
// consumers pick this up without edits. See the .rc-stat-card block in
// globals.css for why the icon stopped being a 48px tinted badge.
/* Phase 1.5 — `brand` is gone and `neutral` is the default.
   The default used to be "neutral", which put an orange glyph on roughly 117
   tiles that never asked for one. globals.css already stops painting it (a
   tile with no matching [data-accent] rule falls through to the muted
   default), so this is a naming fix, not a colour change: the type now says
   what the component actually draws. A tile whose accent genuinely means
   something still opts in explicitly. */
/* d2d-DetectHub eval D1 — a tile carries meaning through `severity` or
   `status`, not through a generic accent. info / success / warning / danger
   are feedback colours: success is the Low hex, dark info is the Info hex,
   dark danger is the Critical hex, and warning sits 6.8 ΔE00 from High. On a
   tile they made "Completed" read as a Low finding. They still render, so
   existing call sites keep working while they migrate; new code uses
   neutral (the spark glyph) or accent, plus severity or status. */
export type StatCardAccent =
  | "neutral"
  | "accent"
  /** @deprecated Use `status` (a state word) or `severity`. */
  | "info"
  /** @deprecated Use `status` (a state word) or `severity`. */
  | "success"
  /** @deprecated Use `status` (a state word) or `severity`. */
  | "warning"
  /** @deprecated Use `status` (a state word) or `severity`. */
  | "danger";

export interface StatCardTrend {
  direction: "up" | "down" | "flat";
  value: ReactNode;
  /* Phase 4.9 — which way is GOOD, stated separately from which way the
     number moved.
     Colour was derived from `direction` alone: up painted green, down red.
     That is right for "assets protected" and exactly backwards for every
     risk metric on this product's dashboards — "Critical alerts ↑ 40%" was
     rendered in success green, and "Mean time to respond ↓" in danger red.
     A dashboard that congratulates you on a spike in critical alerts is
     worse than one with no colour at all.
     Direction stays a fact about the number; sentiment is the editorial
     call, and only the call site knows it. Default "neutral" so nothing
     inherits a judgement nobody made — the 286 existing call sites go grey
     rather than silently keeping a colour that may be wrong. */
  sentiment?: "positive" | "negative" | "neutral";
}

export interface StatCardProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  value: ReactNode;
  label: ReactNode;
  icon?: ReactNode;
  accent?: StatCardAccent;
  /** The tile counts one severity: its glyph takes that rank's --hue-*.
      Any spelling toSeverityTone knows ("Critical", "sev1"). Wins over accent. */
  severity?: string | null;
  /** The figure is a count of things in one state or outcome: the word is
      drawn beside it as a tone badge, read through toOutcomeTone, so a state
      is an outline chip and a risk outcome ("failed") a severity label.
      Colour on a tile's glyph alone can't tell a state from a severity. */
  status?: string | null;
  /** Display text for `status`. Defaults to the value, underscores as spaces. */
  statusLabel?: ReactNode;
  trend?: StatCardTrend;
  /** A line under the figure: a description, a ProgressBar, a severity mix,
      a rank badge. One slot, so a tile with extra context is still this
      component (d2d-DetectHub eval F19: pages hand-built the tile to get it). */
  footer?: ReactNode;
  size?: "sm" | "md";
}

const TREND_GLYPH: Record<StatCardTrend["direction"], string> = { up: "↑", down: "↓", flat: "→" };
const TREND_WORD: Record<StatCardTrend["direction"], string> = { up: "up", down: "down", flat: "unchanged" };

export const StatCard = forwardRef<HTMLDivElement, StatCardProps>(function StatCard(
  { value, label, icon, accent = "neutral", severity, status, statusLabel, trend, footer, size = "md", className, onClick, onKeyDown, ...rest },
  ref
) {
  const interactive = typeof onClick === "function";
  const severityTone = severity ? toSeverityTone(severity) : undefined;
  const interactiveProps = interactive
    ? {
        role: "button" as const,
        tabIndex: 0,
        onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.currentTarget.click();
          }
          onKeyDown?.(e);
        },
      }
    : { onKeyDown };

  return (
    <div
      ref={ref}
      className={cn("rc-stat-card", className)}
      data-accent={accent}
      data-severity={severityTone && severityTone !== "none" ? severityTone : undefined}
      data-size={size}
      onClick={onClick}
      {...interactiveProps}
      {...rest}
    >
      {/* Label and its icon share the top line; the figure sits underneath
          at full size. This is the reverse of the order this component used
          to render, and it is the change that turns a row of tiles into one
          instrument panel instead of six badges with numbers attached — the
          label is what you scan, the number is what you read. */}
      <span className="rc-stat-card__head">
        <span className="rc-stat-card__label">{label}</span>
        {icon ? (
          <span className="rc-stat-card__icon" aria-hidden>
            {icon}
          </span>
        ) : null}
      </span>
      <span className="rc-stat-card__main">
        {status ? (
          <span className="rc-stat-card__figure">
            <span className="rc-stat-card__value">{value}</span>
            <span className="rc-tonebadge rc-stat-card__status" data-tone={toOutcomeTone(status)} data-shape="label">
              <span className="rc-tonebadge__dot" aria-hidden="true" />
              {statusLabel ?? status.replace(/_/g, " ")}
            </span>
          </span>
        ) : (
          <span className="rc-stat-card__value">{value}</span>
        )}
        {trend ? (
          <span
            className="rc-stat-card__trend"
            data-direction={trend.direction}
            data-sentiment={trend.sentiment ?? "neutral"}
          >
            {/* The arrow is decorative; the direction is said in words so
                the trend does not depend on a glyph being announced. */}
            <span aria-hidden>{TREND_GLYPH[trend.direction]}</span>
            <span className="sr-only">{TREND_WORD[trend.direction]} </span>
            {trend.value}
          </span>
        ) : null}
      </span>
      {footer != null && footer !== false ? <div className="rc-stat-card__foot">{footer}</div> : null}
    </div>
  );
});
